/**
 * /auto-sync-subtitles: the second input, and the one request the page makes.
 *
 * The tool page (pages/tool.ts) does everything it does on /shift-subtitles: parse, run the
 * row's `framerate` then `shift`, show the changed cues, download in the dropped format. This
 * module adds what that page lacks: a video or audio file beside the subtitle file, the audio
 * taken out of it IN THE BROWSER (extract-audio.ts, its own bundle), and `POST /api/autosync`
 * with that audio and the cue timings. The answer goes into the row's own boxes
 * (`syncValues`), so the result is the engine's and the person can still adjust it by hand.
 *
 * Every sentence is in the template (`data-autosync-label`); this file picks one and fills its
 * slots, as the tool page does with its parts note.
 */

import { type SyncAnswer, summarise, syncValues } from '../../../../../src/tools/autosync.ts';
import type { ExtractedAudio } from '../extract-audio.ts';
import { ApiError, submitAutosync } from './api.ts';
import { qs } from './dom.ts';
import { classify } from './dropzone.ts';
import { track } from './events.ts';
import { minutesCost, shortFilename } from './format.ts';
import type { OptionValues } from './options-schema.ts';
import { loadPlatforms } from './platforms.ts';
import { showLimitDialog } from './quota.ts';
import { showProblem, showState } from './states.ts';
import { audioDuration, capMegabytes, overCap, within } from './upload.ts';

/** What the tool page lends this module. */
export interface AutosyncHost {
  /** Whether a subtitle file is parsed and ready. */
  hasSubtitles(): boolean;
  /** `[start_s, end_s]` for every cue of the file as dropped, or null when there is none. */
  timings(): Promise<[number, number][] | null>;
  /** Put `values` in the option boxes; `run` re-runs the tool on them. */
  setValues(values: OptionValues, run: boolean): void;
  /** A video in a format nothing here opens (WMV, FLV, MPG…): the page's banner, counted. */
  refuseVideo(file: File): void;
}

export interface AutosyncHandle {
  /** True for a file this page treats as the video or audio rather than a subtitle file. */
  takes(file: File): boolean;
  setMedia(file: File): void;
  /** A new subtitle file arrived or the old one went: a match made for the last one is undone. */
  subtitlesChanged(): void;
  /** Say what is still missing, once the page's state moved (a subtitle file finished parsing). */
  refresh(): void;
  destroy(): void;
}

/** The values that undo a match: no shift, no rescale. */
const UNSYNCED: OptionValues = {
  'shift.delta': 0,
  'framerate.enabled': false,
  'framerate.from': null,
  'framerate.to': null,
};

function extractorUrl(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="build-id"]');
  const build = meta?.content.trim();
  return build
    ? `/static/build/base/extract-audio.js?v=${encodeURIComponent(build)}`
    : '/static/build/base/extract-audio.js';
}

type Extractor = typeof import('../extract-audio.ts');

let extractor: Promise<Extractor> | null = null;

/** The extraction bundle, fetched once, on the first video that needs it. */
function loadExtractor(): Promise<Extractor> {
  extractor ??= import(extractorUrl()) as Promise<Extractor>;
  // A failed fetch is not cached: the next click tries again.
  extractor.catch(() => {
    extractor = null;
  });
  return extractor;
}

export function mountAutosync(
  root: HTMLElement,
  region: HTMLElement,
  host: AutosyncHost,
): AutosyncHandle | null {
  const found = qs<HTMLElement>('[data-autosync]', root);
  if (!found) return null;
  // Bound to a fresh const so the narrowing survives into the closures below.
  const panel: HTMLElement = found;
  const nameSlot = qs<HTMLElement>('[data-autosync-media]', panel);
  const input = qs<HTMLInputElement>('[data-autosync-input]', panel);
  const button = qs<HTMLButtonElement>('[data-autosync-run]', panel);
  const status = qs<HTMLElement>('[data-autosync-status]', panel);

  let media: File | null = null;
  let mediaSeconds: number | null = null;
  let running = false;
  /** A match is in the option boxes. */
  let applied = false;
  /** The status line holds a run's last word (a refusal, a failure): `paint` leaves it. */
  let settled = false;
  /** Bumped by every new media file and every run, so a slow answer cannot land on a newer one. */
  let generation = 0;

  /** One of the template's sentences into the status line, its slots filled. */
  function say(key: string | null, slots: Readonly<Record<string, string>> = {}): void {
    if (!status) return;
    const label =
      key === null ? null : qs<HTMLElement>(`[data-autosync-label="${CSS.escape(key)}"]`, panel);
    if (!label) {
      status.replaceChildren();
      return;
    }
    const copy = label.cloneNode(true) as HTMLElement;
    for (const [slot, value] of Object.entries(slots)) {
      for (const node of copy.querySelectorAll<HTMLElement>(`[data-slot="${CSS.escape(slot)}"]`)) {
        node.textContent = value;
      }
    }
    status.replaceChildren(...Array.from(copy.childNodes));
  }

  /** A word from the template, by key. */
  function word(key: string): string {
    return (
      qs<HTMLElement>(`[data-autosync-label="${CSS.escape(key)}"]`, panel)?.textContent?.trim() ??
      ''
    );
  }

  function paint(): void {
    const subtitles = host.hasSubtitles();
    if (button) button.disabled = running || media === null || !subtitles;
    if (running || applied || settled) return;
    if (media === null && !subtitles) say('need-both');
    else if (media === null) say('need-media');
    else if (!subtitles) say('need-subtitles');
    else if (mediaSeconds !== null) say('ready', { minutes: String(minutesCost(mediaSeconds)) });
    else say('ready-unknown');
  }

  function undo(): void {
    if (!applied) return;
    applied = false;
    host.setValues(UNSYNCED, false);
  }

  function setMedia(file: File): void {
    // Refused before it replaces anything: a video or audio file chosen earlier stays chosen.
    if (classify(file) === 'other-video') {
      host.refuseVideo(file);
      return;
    }
    generation += 1;
    media = file;
    mediaSeconds = null;
    settled = false;
    if (nameSlot) nameSlot.textContent = shortFilename(file.name);
    // A match was for the last video.
    undo();
    paint();
    const mine = generation;
    void audioDuration(file).then((seconds) => {
      if (mine !== generation) return;
      mediaSeconds = seconds;
      paint();
    });
  }

  /** The audio to send: an audio file as it is when it fits, anything else through mediabunny. */
  async function audioOf(
    file: File,
    cap: number | undefined,
    mine: number,
  ): Promise<Blob & { name?: string }> {
    if (classify(file) === 'audio' && !overCap(file.size, cap)) return file;
    say('extracting', { percent: '' });
    const { extractAudio } = await loadExtractor();
    // Progress can still arrive after the conversion settles; it must not
    // overwrite the next line on the status.
    let extracting = true;
    const out: ExtractedAudio = await extractAudio(file, (fraction) => {
      if (extracting && mine === generation) {
        say('extracting', { percent: `${Math.round(fraction * 100)}%` });
      }
    }).finally(() => {
      extracting = false;
    });
    if (out.durationS !== null && mediaSeconds === null) mediaSeconds = out.durationS;
    return new File([out.bytes as BlobPart], out.filename, { type: out.mimeType });
  }

  async function match(): Promise<void> {
    if (running || media === null) return;
    const file = media;
    const mine = ++generation;
    running = true;
    settled = false;
    paint();
    region.replaceChildren();
    try {
      const cues = await host.timings();
      if (cues === null || cues.length === 0) {
        say('need-subtitles');
        return;
      }
      const platforms = await within(loadPlatforms(), 4000, null);
      const cap = platforms?.upload_max_bytes;
      let audio: Blob & { name?: string };
      try {
        audio = await audioOf(file, cap, mine);
      } catch (err) {
        if (mine !== generation) return;
        const name = err instanceof Error ? err.name : '';
        console.error('[autosync] no audio from the file', err);
        settled = true;
        say(name === 'NoAudioTrack' ? 'no-audio' : 'no-extract');
        return;
      }
      if (mine !== generation) return;
      if (overCap(audio.size, cap)) {
        say(null);
        showState(region, 'UPLOAD_TOO_LARGE', { limit_mb: capMegabytes(cap) });
        return;
      }
      say('analysing');
      const answer = await submitAutosync(audio, audio.name ?? 'audio', cues);
      if (mine !== generation) return;
      apply(answer);
    } catch (err) {
      if (mine !== generation) return;
      say(null);
      settled = true;
      if (!(err instanceof ApiError)) {
        console.error('[autosync]', err);
        showState(region, 'NETWORK', {});
        return;
      }
      const problem = err.problem;
      if (problem.code === 'AUTOSYNC_REFUSED' && problem.autosync) {
        // "Nothing was changed": a match from an earlier run comes off too.
        undo();
        say(problem.autosync.reason);
        return;
      }
      if (problem.code === 'QUOTA_EXHAUSTED') {
        showLimitDialog({
          budgetSeconds: problem.quota?.asr_seconds_budget,
          resetsInS: problem.quota?.resets_in_s ?? null,
        });
        track('error_state', { state: 'E10' });
        return;
      }
      const platforms = await within(loadPlatforms(), 2000, null);
      showProblem(
        region,
        problem,
        { limit_mb: capMegabytes(platforms?.upload_max_bytes) },
        { upload: true },
      );
    } finally {
      if (mine === generation) {
        running = false;
        paint();
      }
    }
  }

  function apply(answer: SyncAnswer): void {
    const values = syncValues(answer) as OptionValues;
    applied = true;
    host.setValues(values, true);
    const s = summarise(answer);
    if (s.direction === 'none' && s.rate === null) {
      say('in-sync', { percent: String(s.percent) });
      return;
    }
    const rate = s.rate === null ? '' : rateText(s.rate.from, s.rate.to);
    say('moved', {
      percent: String(s.percent),
      seconds: s.seconds,
      direction: s.direction === 'none' ? '' : word(s.direction),
      rate,
    });
  }

  /** ", and converted from 25 to 23.976 fps", from the template's sentence. */
  function rateText(from: string, to: string): string {
    const label = qs<HTMLElement>('[data-autosync-label="rate"]', panel);
    if (!label) return '';
    const copy = label.cloneNode(true) as HTMLElement;
    const fill = (slot: string, value: string) => {
      const node = copy.querySelector<HTMLElement>(`[data-slot="${slot}"]`);
      if (node) node.textContent = value;
    };
    fill('from', from);
    fill('to', to);
    return copy.textContent ?? '';
  }

  const onPick = () => {
    const file = input?.files?.[0];
    if (file) setMedia(file);
    if (input) input.value = '';
  };
  input?.addEventListener('change', onPick);
  const onClick = () => void match();
  button?.addEventListener('click', onClick);
  panel.hidden = false;
  paint();

  return {
    takes(file) {
      const kind = classify(file);
      // A video this page can't open is still the video, not a subtitle file: it is refused
      // here, so the subtitle file already on the page stays.
      return kind === 'audio' || kind === 'container' || kind === 'other-video';
    },
    setMedia,
    subtitlesChanged() {
      undo();
      settled = false;
      paint();
    },
    refresh: paint,
    destroy() {
      input?.removeEventListener('change', onPick);
      button?.removeEventListener('click', onClick);
    },
  };
}
